import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, TextField, Typography, IconButton, Tooltip, Menu, MenuItem, Chip } from '@mui/material';
import { 
  Image as ImageIcon, 
  FormatBold, 
  FormatItalic, 
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  PhotoSizeSelectSmall,
  PhotoSizeSelectLarge
} from '@mui/icons-material';
import articleApi from '../services/articleApi';
import { toast } from 'react-toastify';

const SimpleRichTextEditor = ({ value, onChange, placeholder = "輸入文章內容..." }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(true);
  const [editValue, setEditValue] = useState('');
  const [imageSize, setImageSize] = useState('medium');
  const [anchorEl, setAnchorEl] = useState(null);
  const textFieldRef = useRef(null);
  const previewRef = useRef(null);

  // 圖片尺寸選項
  const imageSizes = {
    small: { label: '小', width: '200px', icon: <PhotoSizeSelectSmall /> },
    medium: { label: '中', width: '400px', icon: <ImageIcon /> },
    large: { label: '大', width: '600px', icon: <PhotoSizeSelectLarge /> }
  };

  // 初始化編輯值
  useEffect(() => {
    if (isEditing && !editValue) {
      setEditValue(convertHtmlToEditDisplay(value || ''));
    }
  }, [isEditing, value]);

  // 將 HTML 轉換為可視化內容，保持原始尺寸
  const convertHtmlToDisplay = (html) => {
    if (!html) return '';
 
    let display = html;
    // 處理其他基本 HTML 標籤
    display = display
      .replace(/<b>/g, '<strong>')
      .replace(/<\/b>/g, '</strong>')
      .replace(/<i>/g, '<em>')
      .replace(/<\/i>/g, '</em>')
      .replace(/<u>/g, '<span style="text-decoration: underline;">')
      .replace(/<\/u>/g, '</span>')
      .replace(/<br\s*\/?>/g, '<br />')
      .replace(/\n/g, '<br />');
    
    return display;
  };

  // 將 HTML 轉換為編輯模式顯示（圖片用特殊標記）
  const convertHtmlToEditDisplay = (html) => {
    if (!html) return '';
    
    // 處理圖片標籤 - 用特殊標記代替
    let display = html.replace(
      /<div[^>]*><img[^>]+src="([^"]+)"[^>]*><\/div>/g,
      (match, src) => {
        // 從原始 HTML 中提取 width 值
        const widthMatch = match.match(/width:\s*(\d+)px/);
        // 從原始 HTML 中提取 max-width 值
        const maxWidthMatch = match.match(/max-width:\s*(\d+)px/);
        const sizeInfo = maxWidthMatch ? maxWidthMatch[1] : imageSizes[imageSize].width;
        return `[圖片:${src}] width=${sizeInfo}px`;
      }
    );

    return display;
  };

  // 將編輯顯示轉換回 HTML
  const convertEditDisplayToHtml = (editDisplay) => {
    if (!editDisplay) return '';
    // 處理圖片位置標示和尺寸標示
    let html = editDisplay.replace(
      /\[圖片:([^\]]+)\] width=(\d+)px/g,
      (match, src, width) => {
        console.log(src, width);
        return `<div style="margin: 10px 0; text-align: center;"><img src="${src}" alt="上傳的圖片" style="max-width: ${width}px; width: ${width}px; height: auto; margin: 10px auto; display: block;" /></div>`;
      }
    );
    
    return html;
  };

  const displayContent = convertHtmlToDisplay(value || '');
  const editDisplayContent = convertHtmlToEditDisplay(value || '');

  // 獲取實際的 input 元素
  const getInputElement = () => {
    if (textFieldRef.current) {
      return textFieldRef.current.querySelector('textarea') || textFieldRef.current.querySelector('input');
    }
    return null;
  };

  // 在游標位置插入內容
  const insertAtCursor = (textToInsert) => {
    const inputElement = getInputElement();
    if (!inputElement) return;

    const start = inputElement.selectionStart || 0;
    const end = inputElement.selectionEnd || 0;
    const currentValue = inputElement.value;
    
    const newValue = currentValue.substring(0, start) + textToInsert + currentValue.substring(end);
    
    // 更新值
    if (isEditing) {
      setEditValue(newValue);
      onChange(convertEditDisplayToHtml(newValue));
    } else {
      onChange(newValue);
    }
    
    // 設置游標位置到插入內容後面
    const newCursorPosition = start + textToInsert.length;
    
    // 使用 setTimeout 確保 DOM 更新後再設置游標位置
    setTimeout(() => {
      if (inputElement && typeof inputElement.setSelectionRange === 'function') {
        try {
          inputElement.focus();
          inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
        } catch (error) {
          console.warn('無法設置游標位置:', error);
        }
      }
    }, 50);
  };

  const handleImageUpload = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        try {
          setIsUploading(true);
          const response = await articleApi.uploadContentImage(file);
          
          if (isEditing) {
            // 編輯模式：插入圖片標記
            const currentSize = imageSizes[imageSize].width;
            // 將尺寸資訊加入圖片標記中
            const sizeInfo = `width=${currentSize}`;
            insertAtCursor(`[圖片:${response.url}] ${sizeInfo}`);
          } else {
            // 預覽模式：插入 HTML 圖片標籤
            const currentSize = imageSizes[imageSize].width;
            const sizeStyle = `max-width: ${currentSize}; width: ${currentSize}; height: auto; margin: 10px auto; display: block;`;
            insertAtCursor(`<div style="margin: 10px 0; text-align: center;"><img src="${response.url}" alt="上傳的圖片" style="${sizeStyle}" /></div>`);
          }
          
          toast.success('圖片上傳成功');
        } catch (error) {
          console.error('圖片上傳失敗:', error);
          toast.error('圖片上傳失敗');
        } finally {
          setIsUploading(false);
        }
      }
    };
  };

  const handleSizeMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSizeMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSizeSelect = (size) => {
    setImageSize(size);
    handleSizeMenuClose();
  };

  const handlePreviewClick = (e) => {
    if (e.target.tagName === 'IMG') {
      return;
    }
    
    setIsEditing(true);
    setEditValue(editDisplayContent);
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsEditing(false);
    } else if (e.key === 'Enter' && e.ctrlKey) {
      // Ctrl+Enter 保存編輯
      onChange(convertEditDisplayToHtml(editValue));
      setIsEditing(false);
    }
  };

  const handleEditChange = (e) => {
    const newValue = e.target.value;
    setEditValue(newValue);
    
    // 將編輯值轉換回 HTML
    const htmlValue = convertEditDisplayToHtml(newValue);
    onChange(htmlValue);
  };

  const formatButtons = [
    { icon: <FormatBold />, tooltip: '粗體', openTag: '<b>', closeTag: '</b>' },
    { icon: <FormatItalic />, tooltip: '斜體', openTag: '<i>', closeTag: '</i>' },
    { icon: <FormatUnderlined />, tooltip: '底線', openTag: '<u>', closeTag: '</u>' },
    { icon: <FormatListBulleted />, tooltip: '無序列表', openTag: '<ul><li>', closeTag: '</li></ul>' },
    { icon: <FormatListNumbered />, tooltip: '有序列表', openTag: '<ol><li>', closeTag: '</li></ol>' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2">文章內容</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {formatButtons.map((button, index) => (
            <Tooltip key={index} title={button.tooltip}>
              <IconButton
                size="small"
                onClick={() => insertAtCursor(button.openTag + button.closeTag)}
              >
                {button.icon}
              </IconButton>
            </Tooltip>
          ))}
          
          {/* 圖片尺寸選擇 */}
          <Button
            variant="outlined"
            size="small"
            onClick={handleSizeMenuOpen}
            startIcon={imageSizes[imageSize].icon}
          >
            {imageSizes[imageSize].label}
          </Button>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleSizeMenuClose}
          >
            {Object.entries(imageSizes).map(([size, config]) => (
              <MenuItem
                key={size}
                onClick={() => handleSizeSelect(size)}
                selected={imageSize === size}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {config.icon}
                  <Typography>{config.label} ({config.width})</Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>
          
          <Button
            variant="outlined"
            startIcon={<ImageIcon />}
            onClick={handleImageUpload}
            disabled={isUploading}
            size="small"
          >
            {isUploading ? '上傳中...' : '插入圖片'}
          </Button>
          
          <Button
            variant="outlined"
            size="small"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? '預覽模式' : '編輯模式'}
          </Button>
        </Box>
      </Box>

      <Box sx={{ position: 'relative' }}>
        {isEditing ? (
          // 編輯模式（顯示圖片預覽）
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
              編輯模式 - 圖片會顯示為 [圖片:URL] 格式
            </Typography>
            <TextField
              ref={textFieldRef}
              multiline
              rows={30}
              fullWidth
              value={editValue}
              onChange={handleEditChange}
              onKeyDown={handleEditKeyDown}
              placeholder={placeholder}
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '14px'
                }
              }}
              autoFocus
            />
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f9f9f9' }}>
              <Typography variant="subtitle2" gutterBottom>
                圖片預覽（保持原始尺寸）：
              </Typography>
              <div 
                dangerouslySetInnerHTML={{ __html: displayContent }}
                style={{
                  lineHeight: '1.6',
                  fontSize: '16px'
                }}
              />
            </Box>
          </Box>
        ) : (
          // 預覽模式（可點擊編輯）
          <Box
            ref={previewRef}
            onClick={handlePreviewClick}
            sx={{
              minHeight: '300px',
              border: '1px solid #ccc',
              borderRadius: 1,
              p: 2,
              bgcolor: 'white',
              cursor: 'text',
              '&:hover': {
                borderColor: '#1976d2',
                boxShadow: '0 0 0 1px #1976d2'
              }
            }}
          >
            <div 
              dangerouslySetInnerHTML={{ __html: displayContent }}
              style={{
                lineHeight: '1.6',
                fontSize: '16px',
                minHeight: '250px'
              }}
            />
            {!displayContent && (
              <Typography 
                color="text.secondary" 
                sx={{ 
                  fontStyle: 'italic',
                  textAlign: 'center',
                  mt: 4
                }}
              >
                {placeholder}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        提示：您可以使用 HTML 標籤來格式化內容，例如 &lt;b&gt;粗體&lt;/b&gt;、&lt;i&gt;斜體&lt;/i&gt;、&lt;br&gt;換行等
        <br />
        選擇圖片尺寸後上傳，圖片會按照選擇的尺寸插入，預覽中保持原始尺寸
        {isEditing && ' | 編輯模式：圖片顯示為 [圖片:URL] 格式，下方有圖片預覽'}
      </Typography>
    </Box>
  );
};

export default SimpleRichTextEditor;
